import {
  normalizeScopeOfPractice,
  parseDeviceMessage,
  serializeDeviceMessage,
} from "./deviceMessageProtocol";

export const DEVICE_WEBSOCKET_STATE = Object.freeze({
  IDLE: "idle",
  CONNECTING: "connecting",
  OPEN: "open",
  RETRYING: "retrying",
  ERROR: "error",
  DISPOSED: "disposed",
});

const DEFAULT_CONNECTION_TIMEOUT_MS = 15_000;
const DEFAULT_RETRY_BASE_MS = 1_000;
const DEFAULT_RETRY_MAX_MS = 30_000;

const noop = () => {};
const fallbackLogger = {
  debug: noop,
  warn: noop,
};

const getSocketConstant = (WebSocketImpl, name, fallback) => (
  typeof WebSocketImpl?.[name] === "number" ? WebSocketImpl[name] : fallback
);

export const buildDeviceWebSocketUrl = ({
  endpoint,
  token,
  encounterId,
  organizationId,
  scopeOfPractice,
}) => {
  if (!endpoint || !token || !encounterId || !organizationId) {
    throw new Error("Device WebSocket connection parameters are incomplete");
  }

  const endpointWithProtocol = /^wss?:\/\//i.test(endpoint)
    ? endpoint
    : `wss://${endpoint}`;
  const url = new URL(endpointWithProtocol);

  if (url.protocol !== "wss:") {
    throw new Error("Device WebSocket endpoint must use wss");
  }

  url.searchParams.set("token", token);
  url.searchParams.set("encounterId", encounterId);
  url.searchParams.set("organizationId", organizationId);
  url.searchParams.set(
    "scopeOfPractice",
    JSON.stringify(normalizeScopeOfPractice(scopeOfPractice)),
  );

  return url.toString();
};

export const createDeviceWebSocketClient = ({
  endpoint,
  getToken,
  encounterId,
  organizationId,
  scopeOfPractice,
  onMessage,
  onStateChange,
  logger = typeof console !== "undefined" ? console : fallbackLogger,
  WebSocketImpl = typeof WebSocket !== "undefined" ? WebSocket : null,
  connectionTimeoutMs = DEFAULT_CONNECTION_TIMEOUT_MS,
  retryBaseMs = DEFAULT_RETRY_BASE_MS,
  retryMaxMs = DEFAULT_RETRY_MAX_MS,
  random = Math.random,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}) => {
  let socket = null;
  let state = DEVICE_WEBSOCKET_STATE.IDLE;
  let disposed = false;
  let restartRequested = false;
  let retryAttempt = 0;
  let retryTimer = null;
  let connectionTimer = null;

  const connectingState = getSocketConstant(WebSocketImpl, "CONNECTING", 0);
  const openState = getSocketConstant(WebSocketImpl, "OPEN", 1);

  const setState = (nextState, details) => {
    state = nextState;
    onStateChange?.(nextState, details);
  };

  const clearConnectionTimer = () => {
    if (connectionTimer) {
      clearTimer(connectionTimer);
      connectionTimer = null;
    }
  };

  const clearRetryTimer = () => {
    if (retryTimer) {
      clearTimer(retryTimer);
      retryTimer = null;
    }
  };

  const isCurrentSocket = (candidate) => !disposed && socket === candidate;

  let connect;

  const scheduleReconnect = (reason) => {
    if (disposed || retryTimer) return;

    const exponentialDelay = Math.min(
      retryBaseMs * (2 ** retryAttempt),
      retryMaxMs,
    );
    const jitterMultiplier = 0.8 + (random() * 0.4);
    const delay = Math.round(exponentialDelay * jitterMultiplier);
    retryAttempt += 1;

    setState(DEVICE_WEBSOCKET_STATE.RETRYING, { delay, reason });
    logger?.warn?.("[device-ws] Connection interrupted; retry scheduled", {
      encounterId,
      delay,
      reason,
    });

    retryTimer = setTimer(() => {
      retryTimer = null;
      connect();
    }, delay);
  };

  const handleMessage = (event, currentSocket) => {
    if (!isCurrentSocket(currentSocket)) return;

    try {
      const message = parseDeviceMessage(event.data, {
        encounterId,
        organizationId,
      });

      Promise.resolve(onMessage?.(message)).catch((error) => {
        logger?.warn?.("[device-ws] Message callback failed", {
          encounterId,
          messageType: message.type,
          error: error?.message || "Unknown callback error",
        });
      });
    } catch (error) {
      logger?.warn?.("[device-ws] Ignoring invalid message", {
        encounterId,
        error: error?.message || "Unknown protocol error",
      });
    }
  };

  connect = () => {
    if (disposed || !WebSocketImpl) return false;
    if (socket?.readyState === connectingState || socket?.readyState === openState) {
      return true;
    }

    clearRetryTimer();

    const token = typeof getToken === "function" ? getToken() : null;
    let wsUrl;
    try {
      wsUrl = buildDeviceWebSocketUrl({
        endpoint,
        token,
        encounterId,
        organizationId,
        scopeOfPractice,
      });
    } catch (error) {
      setState(DEVICE_WEBSOCKET_STATE.ERROR, { reason: "configuration" });
      logger?.warn?.("[device-ws] Connection configuration is invalid", {
        encounterId,
        error: error?.message || "Unknown configuration error",
      });
      return false;
    }

    let currentSocket;
    try {
      currentSocket = new WebSocketImpl(wsUrl);
    } catch (error) {
      setState(DEVICE_WEBSOCKET_STATE.ERROR, { reason: "construction" });
      logger?.warn?.("[device-ws] Unable to create connection", {
        encounterId,
        error: error?.message || "Unknown connection error",
      });
      scheduleReconnect("construction-error");
      return false;
    }

    socket = currentSocket;
    restartRequested = false;
    setState(DEVICE_WEBSOCKET_STATE.CONNECTING);
    logger?.debug?.("[device-ws] Connecting", { encounterId });

    const handleOpen = () => {
      if (!isCurrentSocket(currentSocket)) {
        currentSocket.close(1000, "Stale connection");
        return;
      }

      clearConnectionTimer();
      retryAttempt = 0;
      setState(DEVICE_WEBSOCKET_STATE.OPEN);
      logger?.debug?.("[device-ws] Connected", { encounterId });
    };

    const handleError = () => {
      if (!isCurrentSocket(currentSocket)) return;
      setState(DEVICE_WEBSOCKET_STATE.ERROR, { reason: "socket-error" });
      logger?.warn?.("[device-ws] Connection error", { encounterId });
    };

    const handleClose = (event) => {
      if (socket !== currentSocket) return;

      clearConnectionTimer();
      socket = null;
      logger?.debug?.("[device-ws] Connection closed", {
        encounterId,
        code: event.code,
      });

      if (disposed) return;
      if (restartRequested) {
        restartRequested = false;
        connect();
        return;
      }

      scheduleReconnect(`close-${event.code || "unknown"}`);
    };

    currentSocket.addEventListener("open", handleOpen);
    currentSocket.addEventListener("message", (event) => handleMessage(event, currentSocket));
    currentSocket.addEventListener("error", handleError);
    currentSocket.addEventListener("close", handleClose);

    connectionTimer = setTimer(() => {
      connectionTimer = null;
      if (!isCurrentSocket(currentSocket) || currentSocket.readyState !== connectingState) {
        return;
      }

      logger?.warn?.("[device-ws] Connection attempt timed out", { encounterId });
      try {
        currentSocket.close(4000, "Connection timeout");
      } catch {
        socket = null;
        scheduleReconnect("connection-timeout");
      }
    }, connectionTimeoutMs);

    return true;
  };

  const reconnect = () => {
    if (disposed) return false;

    clearRetryTimer();
    clearConnectionTimer();
    retryAttempt = 0;

    if (!socket) return connect();

    restartRequested = true;
    try {
      socket.close(1000, "Refreshing connection");
    } catch {
      socket = null;
      restartRequested = false;
      return connect();
    }
    return true;
  };

  const send = (message) => {
    if (disposed || !socket || socket.readyState !== openState) {
      logger?.warn?.("[device-ws] Message not sent because the connection is not open", {
        encounterId,
        messageType: message?.type,
      });
      return false;
    }

    try {
      const serializedMessage = serializeDeviceMessage(message, {
        encounterId,
        organizationId,
      });
      socket.send(serializedMessage);
      return true;
    } catch (error) {
      logger?.warn?.("[device-ws] Message not sent", {
        encounterId,
        messageType: message?.type,
        error: error?.message || "Unknown protocol error",
      });
      return false;
    }
  };

  const dispose = () => {
    if (disposed) return;

    disposed = true;
    restartRequested = false;
    clearRetryTimer();
    clearConnectionTimer();

    const currentSocket = socket;
    socket = null;
    setState(DEVICE_WEBSOCKET_STATE.DISPOSED);

    if (currentSocket) {
      try {
        currentSocket.close(1000, "Visit ended");
      } catch {
        // The browser may already have disposed the underlying connection.
      }
    }
  };

  return {
    connect,
    reconnect,
    send,
    dispose,
    getState: () => state,
  };
};
